import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  User, 
  Mail, 
  FileText, 
  Calendar,
  AlertTriangle,
  ExternalLink,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface OwnershipClaim {
  id: string;
  sequence_id: string;
  claimant_user_id?: string;
  contact_email: string;
  verification_details: string;
  claim_status: 'pending' | 'approved' | 'rejected';
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  sequences?: {
    id: string;
    title: string;
    user_id: string;
    claim_status: string;
    created_at: string;
  };
}

interface ClaimsStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

const ClaimsManagement: React.FC = () => {
  const [claims, setClaims] = useState<OwnershipClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState<OwnershipClaim | null>(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('Insufficient verification');
  const [createAccount, setCreateAccount] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [stats, setStats] = useState<ClaimsStats>({ total: 0, pending: 0, approved: 0, rejected: 0 });
  
  const { toast } = useToast();

  const fetchClaims = useCallback(async (status?: string) => {
    try {
      setLoading(true);
      let query = supabase
        .from('ownership_claims')
        .select(`
          id,
          sequence_id,
          claimant_user_id,
          contact_email,
          verification_details,
          claim_status,
          admin_notes,
          created_at,
          updated_at,
          sequences (
            id,
            title,
            user_id,
            claim_status,
            created_at
          )
        `)
        .order('created_at', { ascending: false });

      if (status && status !== 'all') {
        query = query.eq('claim_status', status);
      }

      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      setClaims(data || []);
      
      // Calculate stats
      if (status === 'all' || !status) {
        const total = data?.length || 0;
        const pending = data?.filter(c => c.claim_status === 'pending').length || 0;
        const approved = data?.filter(c => c.claim_status === 'approved').length || 0;
        const rejected = data?.filter(c => c.claim_status === 'rejected').length || 0;
        setStats({ total, pending, approved, rejected });
      }
      
    } catch (error) {
      console.error('Error fetching claims:', error);
      toast({
        title: "Failed to fetch claims",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchClaims(statusFilter);
  }, [statusFilter, fetchClaims]);

  const handleApproveClaim = async () => {
    if (!selectedClaim) return;
    
    try {
      setProcessing(true);
      
      // Call the approval API through Supabase RPC or direct database update
      const { error } = await supabase.rpc('approve_ownership_claim', {
        claim_id: selectedClaim.id,
        admin_notes: adminNotes,
        create_account: createAccount
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Claim Approved",
        description: `Successfully approved ownership claim for "${selectedClaim.sequences?.title}"`,
        variant: "default"
      });

      // Refresh claims
      await fetchClaims(statusFilter);
      
      // Close dialog and reset state
      setShowApprovalDialog(false);
      setSelectedClaim(null);
      setAdminNotes('');
      setCreateAccount(false);
      
    } catch (error) {
      console.error('Error approving claim:', error);
      toast({
        title: "Failed to approve claim",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClaim = async () => {
    if (!selectedClaim) return;
    
    try {
      setProcessing(true);
      
      // Call the rejection API through Supabase RPC or direct database update
      const { error } = await supabase.rpc('reject_ownership_claim', {
        claim_id: selectedClaim.id,
        reason: rejectionReason,
        admin_notes: adminNotes
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Claim Rejected",
        description: `Successfully rejected ownership claim for "${selectedClaim.sequences?.title}"`,
        variant: "default"
      });

      // Refresh claims
      await fetchClaims(statusFilter);
      
      // Close dialog and reset state
      setShowRejectionDialog(false);
      setSelectedClaim(null);
      setAdminNotes('');
      setRejectionReason('Insufficient verification');
      
    } catch (error) {
      console.error('Error rejecting claim:', error);
      toast({
        title: "Failed to reject claim",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200"><Clock size={12} className="mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle size={12} className="mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><XCircle size={12} className="mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const openApprovalDialog = (claim: OwnershipClaim) => {
    setSelectedClaim(claim);
    setAdminNotes('');
    setCreateAccount(!claim.claimant_user_id); // Auto-suggest account creation if no user ID
    setShowApprovalDialog(true);
  };

  const openRejectionDialog = (claim: OwnershipClaim) => {
    setSelectedClaim(claim);
    setAdminNotes('');
    setRejectionReason('Insufficient verification');
    setShowRejectionDialog(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Ownership Claims Management</h2>
          <p className="text-gray-600">Review and process sequence ownership claims from users</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchClaims(statusFilter)}
          disabled={loading}
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Claims</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <Clock size={14} />
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <CheckCircle size={14} />
              Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <XCircle size={14} />
              Rejected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter size={16} />
          <Label htmlFor="status-filter">Filter by Status:</Label>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Claims</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Claims List */}
      <div className="space-y-4">
        {claims.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Claims Found</h3>
              <p className="text-gray-600">
                {statusFilter === 'all' 
                  ? 'No ownership claims have been submitted yet.'
                  : `No ${statusFilter} claims found.`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          claims.map((claim) => (
            <Card key={claim.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">
                          {claim.sequences?.title || 'Unknown Sequence'}
                        </h3>
                        {getStatusBadge(claim.claim_status)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar size={14} />
                        {new Date(claim.created_at).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Claimant Info */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <User size={14} />
                        <span className="font-medium">Claimant:</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail size={14} />
                        <span>{claim.contact_email}</span>
                      </div>
                      {claim.claimant_user_id && (
                        <span className="text-green-600 text-xs bg-green-50 px-2 py-1 rounded">
                          Existing User
                        </span>
                      )}
                    </div>

                    {/* Verification Details */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={14} />
                        <span className="text-sm font-medium">Verification Details:</span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {claim.verification_details}
                      </p>
                    </div>

                    {/* Admin Notes */}
                    {claim.admin_notes && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText size={14} />
                          <span className="text-sm font-medium text-blue-800">Admin Notes:</span>
                        </div>
                        <p className="text-sm text-blue-700 whitespace-pre-wrap">
                          {claim.admin_notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {claim.claim_status === 'pending' && (
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => openApprovalDialog(claim)}
                      disabled={processing}
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openRejectionDialog(claim)}
                      disabled={processing}
                    >
                      <XCircle size={16} className="mr-2" />
                      Reject
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/sequence/${claim.sequence_id}`, '_blank')}
                    >
                      <ExternalLink size={16} className="mr-2" />
                      View Sequence
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-600" />
              Approve Ownership Claim
            </DialogTitle>
            <DialogDescription>
              You're about to approve the ownership claim for "{selectedClaim?.sequences?.title}".
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!selectedClaim?.claimant_user_id && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="createAccount"
                  checked={createAccount}
                  onChange={(e) => setCreateAccount(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="createAccount" className="text-sm">
                  Create user account for {selectedClaim?.contact_email}
                </Label>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="adminNotes">Admin Notes (Optional)</Label>
              <Textarea
                id="adminNotes"
                placeholder="Add any notes about this approval..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowApprovalDialog(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveClaim}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Approve Claim'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectionDialog} onOpenChange={setShowRejectionDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle size={20} className="text-red-600" />
              Reject Ownership Claim
            </DialogTitle>
            <DialogDescription>
              You're about to reject the ownership claim for "{selectedClaim?.sequences?.title}".
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rejectionReason">Rejection Reason *</Label>
              <Select value={rejectionReason} onValueChange={setRejectionReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Insufficient verification">Insufficient verification</SelectItem>
                  <SelectItem value="Invalid claim">Invalid claim</SelectItem>
                  <SelectItem value="Disputed ownership">Disputed ownership</SelectItem>
                  <SelectItem value="Incomplete information">Incomplete information</SelectItem>
                  <SelectItem value="Duplicate claim">Duplicate claim</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="adminNotesReject">Additional Notes (Optional)</Label>
              <Textarea
                id="adminNotesReject"
                placeholder="Add any additional notes about this rejection..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectionDialog(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectClaim}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Reject Claim'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClaimsManagement; 